import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ChevronRight, Cross, InfoCircle, Mail } from '@saleshandy/icons';
import { Button } from '@saleshandy/design-system';
import durationOptions, {
  getDateRange,
} from '../../../../shared/components/date-filter/date-filter-options';
import { isRequestPending } from '../../../settings/components/billing-subscription/utils/helper';
import TestReportGraph from '../test-report/components/test-report-graph';
import TestReportContent from '../test-report/components/test-report-content';
import type { IProps } from './inbox-radar-shareable-report-container';
import { SHLogoDarkSM } from '../../../../shared/svg';
import {
  OverlayPopover,
  OverlayTooltip,
  Placement,
} from '../../../../shared/design-system/components/overlay';
import { accessibleOnClick } from '../../../../shared/utils/accessible-on-click';
import { DatePickerOption } from '../../../../shared/components/date-filter/types';
import {
  executeOnRequestStatus,
  getIsRequestSucceeded,
} from '../../../../shared/utils';
import { transformGraphDataResponse } from '../ir-graph/utils/helper';
import HelmetIcon from '../../../../shared/components/helmet-icon';
import { TestType } from '../../enums/inbox-radar';
import { constants } from '../../../../shared/enums/constants';
import EmailViewModal from '../test-report/components/test-report-header/email-view-modal';
import { TestTypeDisplayName } from '../dashboard/utils/helper';
import { getFormattedDate } from '../../../settings/components/users-and-teams/utils/get-formatted-date';
import DemoAccountBanner from '../../../home/components/demo-account-banner';
import { UserRole } from '../../../../shared/enums/user-roles';
import toaster, { Theme } from '../../../../shared/toaster';

const InboxRadarShareableReport: React.FC<IProps> = ({
  agencyConfig,
  getShareableReportDataResponse,
  getShareableReportSenderEmailAccountsResponse,

  agencyConfigRequestStatus,
  getShareableReportDataRequestStatus,
  getShareableReportSenderEmailAccountsRequestStatus,

  getShareableReportDataRequestError,
  getShareableReportSenderEmailAccountsRequestError,

  sendGetShareableReportDataRequest,
  sendGetShareableReportSenderEmailAccountsRequest,
  sendGetAgencyConfigRequest,

  resetGetShareableReportDataRequest,
  resetGetShareableReportSenderEmailAccountsRequest,
}) => {
  const { templateId = '', runId = '' } = useParams<{
    templateId: string;
    runId: string;
  }>();
  const history = useHistory();

  const [dateFilters, setDateFilters] = useState<{
    date: {
      startDate: DateTime;
      endDate: DateTime;
    };
  }>({
    date: durationOptions[4],
  });

  const [pageFilters, setPageFilters] = useState<{
    page: number;
    limit: number;
  }>({
    page: 1,
    limit: constants.INBOX_RADAR_TEST_REPORT_PAGE_SIZE,
  });
  const popoverRef = useRef<HTMLDivElement>(null);
  const [isConfigPopoverOpen, setIsConfigPopoverOpen] = useState(false);

  const [shareableReportAnalytics, setShareableReportAnalytics] = useState<any>(
    null,
  );
  const [
    shareableReportTestConfig,
    setShareableReportTestConfig,
  ] = useState<any>(null);
  const [shareableReportSummary, setShareableReportSummary] = useState<any>(
    null,
  );
  const [shareableReportEsps, setShareableReportEsps] = useState<any>(null);
  const [isEmailViewModalOpen, setIsEmailViewModalOpen] = useState(false);
  const initalDataLoaded = useRef(false);

  const agencyConfigPayload = {
    baseUrl: window.location.origin,
  };

  const isLoadingMoreSenderEmailAccounts =
    isRequestPending(getShareableReportSenderEmailAccountsRequestStatus) &&
    pageFilters.page > 1;

  const handleClickAndScrollOutside = useCallback(
    (event: MouseEvent | Event) => {
      if (
        popoverRef?.current &&
        !popoverRef?.current?.contains(event.target as Node)
      ) {
        setIsConfigPopoverOpen(false);
      }
    },
    [popoverRef],
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickAndScrollOutside);
    document.addEventListener('scroll', handleClickAndScrollOutside, true);

    return () => {
      document.removeEventListener('mousedown', handleClickAndScrollOutside);
      document.removeEventListener('scroll', handleClickAndScrollOutside, true);
    };
  }, [handleClickAndScrollOutside]);

  useEffect(() => {
    sendGetAgencyConfigRequest(agencyConfigPayload);
  }, []);

  const onDateTimeSelectionChange = (
    label: string,
    startDate: DateTime,
    endDate: DateTime,
    displayText: string,
  ) => {
    const userZone = DateTime.local().zoneName;

    if (displayText !== 'Custom') {
      const { startDate: newStartDate, endDate: newEndDate } = getDateRange(
        label,
        userZone,
      );
      startDate = newStartDate;
      endDate = newEndDate;
    }

    const newDateFilter: DatePickerOption = {
      label,
      startDate,
      endDate,
      displayText,
      key: label,
      value: label,
    };
    setDateFilters({ ...dateFilters, date: newDateFilter });
  };

  useEffect(() => {
    if (templateId && runId) {
      sendGetShareableReportDataRequest(templateId, {
        run: Number(runId),
        startDate: dateFilters.date.startDate.toISO(),
        endDate: dateFilters.date.endDate.toISO(),
      });
      sendGetShareableReportSenderEmailAccountsRequest(templateId, {
        run: Number(runId),
        limit: pageFilters.limit,
        page: 1,
      });
    }
    // Cleanup function to reset state on component unmount
    return () => {
      resetGetShareableReportDataRequest();
      resetGetShareableReportSenderEmailAccountsRequest();
      setPageFilters({
        page: 1,
        limit: constants.INBOX_RADAR_TEST_REPORT_PAGE_SIZE,
      });
    };
  }, [
    templateId,
    runId,
    sendGetShareableReportDataRequest,
    sendGetShareableReportSenderEmailAccountsRequest,
    resetGetShareableReportDataRequest,
    resetGetShareableReportSenderEmailAccountsRequest,
  ]);

  useEffect(() => {
    if (templateId && runId && pageFilters.page > 1) {
      sendGetShareableReportSenderEmailAccountsRequest(templateId, {
        run: Number(runId),
        limit: pageFilters.limit,
        page: pageFilters.page,
      });
    }
  }, [
    pageFilters.page,
    templateId,
    runId,
    pageFilters.limit,
    sendGetShareableReportSenderEmailAccountsRequest,
  ]);

  useEffect(() => {
    executeOnRequestStatus({
      status: getShareableReportDataRequestStatus,
      onSuccess: () => {
        if (!initalDataLoaded.current) {
          initalDataLoaded.current = true;
        }
        if (getShareableReportDataResponse?.analytics) {
          const graphData = transformGraphDataResponse(
            getShareableReportDataResponse?.analytics,
          );
          setShareableReportAnalytics(graphData);
        }
        setShareableReportTestConfig(
          getShareableReportDataResponse?.testConfig,
        );
        setShareableReportSummary({
          summary: getShareableReportDataResponse?.summary,
          inboxPlacement: getShareableReportDataResponse?.inboxPlacement,
        });
        setShareableReportEsps(
          getShareableReportDataResponse?.espToEspPlacement,
        );
      },
      onFailed: () => {
        resetGetShareableReportDataRequest();
      },
    });
  }, [getShareableReportDataRequestStatus, getShareableReportDataRequestError]);

  useEffect(() => {
    executeOnRequestStatus({
      status: getShareableReportSenderEmailAccountsRequestStatus,
      onFailed: () => {
        resetGetShareableReportSenderEmailAccountsRequest();
      },
    });
  }, [
    getShareableReportSenderEmailAccountsRequestStatus,
    getShareableReportSenderEmailAccountsRequestError,
    isLoadingMoreSenderEmailAccounts,
  ]);

  useEffect(() => {
    if (templateId && runId && initalDataLoaded.current) {
      sendGetShareableReportDataRequest(templateId, {
        run: Number(runId),
        startDate: dateFilters.date.startDate.toISO(),
        endDate: dateFilters.date.endDate.toISO(),
      });
    }
  }, [dateFilters]);

  const handleLoadMoreSenderEmailAccounts = () => {
    if (
      getShareableReportSenderEmailAccountsResponse?.hasMore &&
      !isLoadingMoreSenderEmailAccounts
    ) {
      setPageFilters((prevFilters) => ({
        ...prevFilters,
        page: prevFilters.page + 1,
      }));
    }
  };

  const getTestFrequencyText = () => {
    if (shareableReportTestConfig?.type === TestType.MANUAL_EXTERNAL) {
      return TestTypeDisplayName[shareableReportTestConfig?.type];
    }
    if (shareableReportTestConfig?.type === TestType.MANUAL_SEQUENCE) {
      return 'Runs only one-time';
    }
    return shareableReportTestConfig?.schedule;
  };

  return (
    <>
      {getIsRequestSucceeded(agencyConfigRequestStatus) && (
        <HelmetIcon icon={agencyConfig?.favicon} />
      )}
      <div className="test-report-container inbox-radar-shareable-report">
        {shareableReportTestConfig?.userRole === UserRole.DemoClientUser && (
          <DemoAccountBanner />
        )}
        <div className="inbox-radar-shareable-report-header">
          <SHLogoDarkSM />
          <div className="inbox-radar-shareable-report-header__title">
            <div className="d-flex align-items-center gap-4">
              <span>Inbox Radar</span>
              <ChevronRight width={16} height={16} className="right-icon" />
              {shareableReportTestConfig?.templateName?.length > 40 ? (
                <OverlayTooltip
                  text={shareableReportTestConfig?.templateName}
                  placement={Placement.Bottom}
                >
                  <span>
                    {shareableReportTestConfig?.templateName?.slice(0, 40)}...
                  </span>
                </OverlayTooltip>
              ) : (
                <span>{shareableReportTestConfig?.templateName}</span>
              )}
              <OverlayPopover
                show={isConfigPopoverOpen}
                className="test-report-config-popover"
                onToggle={() => setIsConfigPopoverOpen(!isConfigPopoverOpen)}
                content={
                  <>
                    <div
                      ref={popoverRef}
                      className="test-config-popover-content"
                    >
                      <div className="test-config-item">
                        <span className="label">
                          Test{' '}
                          {`${
                            shareableReportTestConfig?.type ===
                            TestType.MANUAL_EXTERNAL
                              ? 'Type'
                              : 'Frequency'
                          }:`}
                        </span>
                        <span className="value">{getTestFrequencyText()}</span>
                      </div>
                      <div className="test-config-item">
                        <span className="label">Last Run on:</span>
                        <span className="value">
                          {getFormattedDate(
                            shareableReportTestConfig?.activatedAt,
                            DateTime.local().zoneName,
                          )}
                        </span>
                      </div>
                      <div className="test-config-item">
                        <span className="label">Created by:</span>
                        <span className="value">
                          {shareableReportTestConfig?.userName}
                        </span>
                      </div>
                      {shareableReportTestConfig?.type !==
                        TestType.MANUAL_EXTERNAL && (
                        <>
                          <div className="test-config-item">
                            <span className="label">Sequence Name:</span>
                            <span className="value">
                              {shareableReportTestConfig?.sequenceName}
                            </span>
                          </div>
                          <div className="test-config-item">
                            <span className="label">Step:</span>
                            <span className="value">
                              {shareableReportTestConfig?.stepNumber}
                            </span>
                          </div>
                          <div className="test-config-item">
                            <span className="label">Variant:</span>
                            <span className="value">
                              {shareableReportTestConfig?.variantNumber}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                    <div
                      className="test-config-popover-close-button"
                      {...accessibleOnClick(() => {
                        setIsConfigPopoverOpen(false);
                      })}
                    >
                      <Cross className="cross-icon" />
                    </div>
                  </>
                }
                placement={Placement.Bottom}
              >
                <div
                  className="test-report-header-left-info"
                  {...accessibleOnClick(() => {
                    setIsConfigPopoverOpen(true);
                  })}
                >
                  <InfoCircle className="info-icon" />
                </div>
              </OverlayPopover>
            </div>
            <div className="d-flex align-items-center gap-16 shareable-report-header-right">
              {(shareableReportTestConfig?.type === TestType.RECURRING ||
                shareableReportTestConfig?.type === TestType.MANUAL_SEQUENCE) &&
                shareableReportTestConfig?.isEmailPreviewAvailable && (
                  <>
                    <div
                      className="d-flex align-items-center gap-4 view-email-btn"
                      {...accessibleOnClick(() => {
                        if (
                          shareableReportTestConfig?.sequenceDeletedAt ||
                          shareableReportTestConfig?.stepDeletedAt ||
                          shareableReportTestConfig?.variantDeletedAt
                        ) {
                          toaster.error(
                            'Email preview is not available because the sequence step has been deleted.',
                            { theme: Theme.New },
                          );
                          return;
                        }
                        setIsEmailViewModalOpen(true);
                      })}
                    >
                      <Mail height={16} width={16} className="mail-icon" />
                      <span>View Email</span>
                    </div>
                    <div className="shareable-report-header-right-divider" />
                  </>
                )}
              <Button
                variant="primary"
                className="explore-saleshandy-btn"
                {...accessibleOnClick(() => {
                  history.push('/login');
                })}
              >
                Sign up for free
              </Button>
            </div>
          </div>
        </div>
        <div className="test-report-content-container inbox-radar-shareable-report-container">
          {shareableReportTestConfig?.type === TestType.RECURRING &&
            shareableReportAnalytics && (
              <TestReportGraph
                filters={dateFilters}
                graphData={shareableReportAnalytics}
                onDateTimeSelectionChange={onDateTimeSelectionChange}
                isLoading={isRequestPending(
                  getShareableReportDataRequestStatus,
                )}
              />
            )}

          <TestReportContent
            isLoading={
              isRequestPending(
                getShareableReportSenderEmailAccountsRequestStatus,
              ) &&
              pageFilters.page === 1 &&
              !isLoadingMoreSenderEmailAccounts
            }
            forShareableReport={true}
            testConfigData={shareableReportTestConfig}
            testReportSummaryData={shareableReportSummary}
            testReportListData={getShareableReportSenderEmailAccountsResponse}
            onLoadMore={handleLoadMoreSenderEmailAccounts}
            isLoadingMore={isLoadingMoreSenderEmailAccounts}
            testReportEspsData={shareableReportEsps}
          />
        </div>
        {isEmailViewModalOpen && (
          <EmailViewModal
            show={isEmailViewModalOpen}
            onClose={() => setIsEmailViewModalOpen(false)}
            emailViewData={{
              isSubsequence: shareableReportTestConfig?.isSubsequence,
              sequenceName: shareableReportTestConfig?.sequenceName,
              stepNumber: shareableReportTestConfig?.stepNumber,
              variantNumber: shareableReportTestConfig?.variantNumber,
              previewContent: {
                body: shareableReportTestConfig?.previewContent?.body,
                subject: shareableReportTestConfig?.previewContent?.subject,
              },
            }}
          />
        )}
      </div>
    </>
  );
};

export default InboxRadarShareableReport;
